import { useGlobalSearchStore } from "client/Features/Layout/TitleBar/GlobalSearch/globalSearchStore";
import {
	forwardRef,
	ReactElement,
	Ref,
	RefObject,
	useCallback,
	useContext,
	useEffect,
	useImperativeHandle,
	useMemo,
	useRef,
	useState,
	KeyboardEvent
} from "react";
import {
	isNotNil,
	setTrackingId
} from "client/Misc/utils";
import { shallowComparison } from "client/Misc/compareUtils";
import {
	ResultStatus,
	ResultView
} from "../ResultView/ResultView";
import {
	BusyIndicator,
	Button,
	DescendantRootsRegistration,
	DropOverContext,
	DropOverContextData
} from "client/Components";
import {
	UsePointerEventOptions,
	usePointerEventOutside,
	useIsTouchDevice,
	useAlerts,
	AlertManager,
	useUnmountedCancellation,
	useKeyboardListNavigation,
	KeyBoardListNavigationContext
} from "client/Hooks";
import styles from "./SearchAndResults.module.less";
import {
	SearchFormOptions,
	ScopeActions,
	AvailableActionsIdentifier
} from "../GlobalSearch";
import {
	deleteSearchTerms,
	SearchNodeModel,
	GlobalSearchTermModel,
	listSearchTerms
} from "client/Api/TitleBar/GlobalSearch/globalSearch";
import { RecentSearchView } from "../RecentSearchView/RecentSearchView";
import { localize } from "client_legacy/ndaw/culture";
import {
	isCancelledError
} from "client_legacy/ndaw/cancellation";
import { WorkspaceHeaderStyle } from "client_legacy/ndaw/workspaceAbilitiesStatusProvider";
import classNames from "classnames";
import { SearchFormHandle } from "../SearchForm/SearchForm";
import { sortBy } from "client_legacy/ndaw/arrayHelpers";
import { getSearchUrl } from "client/Features/Search/routing";
import { navigateTo } from "client_legacy/ndaw/navigator";
import { TimeZone } from "client/DateAndTime/timezones";
import { getGlobalSearchQueryParamsOptions } from "client/Features/Search/SearchPage";
import { FrequentlyUsedTileModel } from "client/Api/Workspace/workspace";
import { useRecentlyOpenedResults } from "client/Features/Search/RecentSearch/Persistence/useRecentlyOpenedResults";
import { ContactSource } from "client/Api/Email/contacts";
import { SortDirection } from "client/Api/common";
import { FrequentlyUsedAppsHandle } from "../RecentSearchView/FrequentlyUsedAppList";
import { hasImplicitDependencyOn } from "client/Misc/reactUtils";
import { tryResolveKeyboardKey } from "client/Misc/keyboardUtils";
import { Key } from "client/Misc/commonTypes";

export type SearchAndResultsHandle = {
	open(): void;
};

export type SearchAndResultsProps = {
	nodes: readonly SearchNodeModel[];
	headerStyle: WorkspaceHeaderStyle;
	onSearch(searchTerm: string): void;
	availableShowMoreOptions: ReadonlyMap<AvailableActionsIdentifier, ScopeActions>;
	renderSearchForm(
		options: Omit<SearchFormOptions, "isNewGlobalNavigationLayoutActive" | "effectiveHeaderStyle">
	): ReactElement;
	timezone: TimeZone;
	onInvokeFrequentlyUsedTile: (item: FrequentlyUsedTileModel) => void;
	onOpenContactDetails: (id: string, source: ContactSource) => void;
};

export const SearchAndResults = forwardRef(
	(props: SearchAndResultsProps, ref: Ref<SearchAndResultsHandle>): ReactElement => {
		const {
			nodes,
			headerStyle,
			onSearch,
			availableShowMoreOptions,
			renderSearchForm,
			timezone,
			onInvokeFrequentlyUsedTile,
			onOpenContactDetails
		} = props;

		const [isShowingResults, setIsShowingResults] = useState<boolean>(false);
		const [isBusy, setIsBusy] = useState<boolean>(false);
		const [tabbableItem, setTabbableItem] = useState<TabbableItem | null>({
			index: 0,
			kind: "result-item"
		});

		const ignoreFocusEventRef: RefObject<boolean> = useRef<boolean>(false);

		const {
			searchTerm,
			selectedNode,
			setSelectedNode,
			resultState,
			recentSearchQueries,
			setRecentSearchQueries
		} = useGlobalSearchStore(
			(x) => ({
				searchTerm: x.searchTerm,
				selectedNode: x.selectedNode,
				setSelectedNode: x.setSelectedNode,
				resultState: x.resultState,
				recentSearchQueries: x.recentSearchQueries,
				setRecentSearchQueries: x.setRecentSearchQueries
			}),
			shallowComparison
		);

		const actualRecentSearchQueries = useMemo(
			(): readonly GlobalSearchTermModel[] => {
				return getActualRecentSearchQueries(recentSearchQueries);
			},
			[recentSearchQueries]
		);

		const {
			items: recentlyOpenedItems,
			clear: clearRecentlyOpenedItems,
			remove: removeRecentlyOpenedItem
		} = useRecentlyOpenedResults();

		const rootRef: RefObject<HTMLDivElement | null> = useRef<HTMLDivElement>(null);
		const formRef: RefObject<SearchFormHandle | null> = useRef<SearchFormHandle>(null);
		const frequentlyUsedAppsRef: RefObject<FrequentlyUsedAppsHandle | null> = useRef<FrequentlyUsedAppsHandle>(null);

		const isTouch: boolean = useIsTouchDevice();
		const globalCancellation: AbortSignal = useUnmountedCancellation();

		const focusInputHandler = useCallback(
			(): void => {
				formRef.current
					?.focusSearchInput();
			},
			[]
		);

		const onOutOfBoundsHandler = useCallback(
			(direction: "start" | "end"): void => {
				if (direction === "end") {
					frequentlyUsedAppsRef.current
						?.focusFirstItem();
				}
			},
			[]
		);

		const {
			contextData: keyboardNavigationContext,
			focusFirstItemHandler,
			focusItemAtIndexHandler
		} = useKeyboardListNavigation({
			onFocusInput: focusInputHandler,
			onOutOfBounds: onOutOfBoundsHandler,
			onItemFocusChange: (index: number): void => setTabbableItem({ index, kind: "result-item" }),
			onCancel: (): void => {
				setIsShowingResults(false);
			},
			shouldCancelDirectlyFromItems: true,
		});

		const navigateToResultsHandler = useCallback(
			(): void => {
				setIsShowingResults(true);

				// Delay to ensure the results are rendered before focusing the first item
				setTimeout(
					(): void => {
						focusFirstItemHandler();
					},
					0
				);
			},
			[focusFirstItemHandler]
		);

		const [descendantsRootRegistrations, setDescendantsRootRegistration] = useState<readonly DescendantRootsRegistration[]>([]);
		const alertManager: AlertManager = useAlerts();

		const parentContext: DropOverContextData = useContext(DropOverContext);

		const otherRoots = useMemo(
			(): readonly HTMLElement[] => {
				const result: HTMLElement[] = [];

				for (const registration of descendantsRootRegistrations) {
					result.push(
						...registration
							.roots
							.filter(isNotNil)
					);
				}

				return result;
			},
			[descendantsRootRegistrations]
		);

		useImperativeHandle(
			ref,
			(): SearchAndResultsHandle => ({
				open: () => setIsShowingResults(true),
			}),
			[]
		);

		useEffect(
			(): (() => void) | undefined => {
				const roots: (HTMLElement | nil)[] = [
					rootRef.current,
					...otherRoots
				];

				return parentContext.registerDescendantsRoots?.(roots);
			},
			[otherRoots, parentContext]
		);

		useEffect(
			() => {
				const fetchRecentSearchQueries = async(signal: AbortSignal):Promise<void> => {
					try {
						const response: readonly GlobalSearchTermModel[] = await listSearchTerms(signal);
						setRecentSearchQueries(response);
					} catch(e) {
						console.error(e);
					}
				};

				const cancellation = new AbortController();
				fetchRecentSearchQueries(cancellation.signal);
			},
			[setRecentSearchQueries]
		);

		useEffect(
			(): void => {
				hasImplicitDependencyOn(searchTerm, selectedNode);
				setTabbableItem({ index: 0, kind: "result-item" });
			},
			[searchTerm, selectedNode]
		);

		const deleteSearchQueryHandler = useCallback(
			async (id: number): Promise<void> => {
				try {
					if (isBusy) {
						return;
					}

					setIsBusy(true);
					await deleteSearchTerms([id], globalCancellation);

					setRecentSearchQueries(
						recentSearchQueries.filter(q => q.id !== id)
					);
				} catch(e) {
					if (isCancelledError(e)) {
						return;
					}

					alertManager.showError(localize("_GeneralErrorMessage"));
					console.error(e);
				}
				setIsBusy(false);
			},
			[
				isBusy,
				globalCancellation,
				setRecentSearchQueries,
				recentSearchQueries,
				alertManager
			]
		);

		const clearSearchQueriesHandler = useCallback(
			async (): Promise<void> => {
				try {
					if (isBusy) {
						return;
					}

					setIsBusy(true);
					await deleteSearchTerms(recentSearchQueries.map(q => q.id), globalCancellation);

					setRecentSearchQueries([]);
				} catch(e) {
					if (isCancelledError(e)) {
						return;
					}

					alertManager.showError(localize("_GeneralErrorMessage"));
					console.error(e);
				}

				setIsBusy(false);
			},
			[
				alertManager,
				globalCancellation,
				isBusy,
				recentSearchQueries,
				setRecentSearchQueries
			]
		);

		const pointerEventOptions = useMemo(
			(): UsePointerEventOptions => {
				return {
					otherRoots,
					shouldListenForEntireClick: false
				};
			},
			[otherRoots]
		);

		const dismissHandler = useCallback(
			(): void => {
				setIsShowingResults(false);
			},
			[]
		);

		usePointerEventOutside(
			dismissHandler,
			rootRef.current,
			pointerEventOptions
		);

		const contentContext: DropOverContextData = useMemo(
			(): DropOverContextData => ({
				registerDescendantsRoots: (roots) => {
					const registration: DescendantRootsRegistration = {
						roots: roots
					};
					setDescendantsRootRegistration(x => [...x, registration]);

					return (): void => {
						setDescendantsRootRegistration(
							s => s.filter(x => x !== registration)
						);
					};
				},
				onClose: dismissHandler
			}),
			[dismissHandler]
		);

		const searchHandler = useCallback(
			(searchTerm: string): void => {
				onSearch(searchTerm);

				if (isTouch) {
					formRef.current?.blurSearchInput();
				}
			},
			[
				isTouch,
				onSearch
			]
		);

		const advancedSearchHandler = useCallback(
			(): void => {
				const url: string = getSearchUrl(
					getGlobalSearchQueryParamsOptions(
						searchTerm,
						selectedNode
					)
				);

				navigateTo(url);
			},
			[
				selectedNode,
				searchTerm
			]
		);

		const openResultsOnInputFocusHandler = useCallback(
			(): void => {
				if (ignoreFocusEventRef.current) {
					return;
				}

				setIsShowingResults(true);
			},
			[]
		);

		const searchFormOptions = useMemo(
			(): Omit<SearchFormOptions, "isNewGlobalNavigationLayoutActive" | "effectiveHeaderStyle"> => ({
				onSearch: searchHandler,
				lastUsedSearchTerm: searchTerm,
				inputRef: formRef,
				onNavigateToResults: navigateToResultsHandler,
				onFocusInput: openResultsOnInputFocusHandler,
				onCloseResults: () => setIsShowingResults(false)
			}),
			[
				openResultsOnInputFocusHandler,
				navigateToResultsHandler,
				searchHandler,
				searchTerm
			]
		);

		const closeResultsKeydownHandler = useCallback(
			(args: KeyboardEvent<HTMLDivElement>): void => {
				const key: Key | null = tryResolveKeyboardKey(args);

				if (key === Key.Escape) {
					ignoreFocusEventRef.current = true;

					focusInputHandler();
					setIsShowingResults(false);

					ignoreFocusEventRef.current = false;
				}
			},
			[focusInputHandler]
		);

		const handleRetry = (): void => {
			onSearch(searchTerm);
		};

		const shouldRenderResults: boolean = isShowingResults;
		const shouldRenderBorder: boolean = resultState.status === ResultStatus.Success
			&& resultState.results.length > 0;

		return (
			<div
				{...setTrackingId("global-search")}
				ref={rootRef}
				className={classNames(
					styles.root,
					{[styles.root_coloredHeader]: headerStyle === WorkspaceHeaderStyle.ColoredBackground}
				)}
			>
				<div className={styles.formWrapper}>
					{renderSearchForm(searchFormOptions)}
					{shouldRenderResults && (
						<div
							className={styles.resultsWrapper}
							onKeyDown={closeResultsKeydownHandler}
						>
							<DropOverContext.Provider value={contentContext}>
								<KeyBoardListNavigationContext.Provider value={keyboardNavigationContext}>
									{
										searchTerm
											? (
												<ResultView
													nodes={nodes}
													selectedNode={selectedNode}
													onSelectedNodeChanged={setSelectedNode}
													data={resultState}
													onFocusInput={focusInputHandler}
													onRetry={handleRetry}
													availableShowMoreOptions={availableShowMoreOptions}
													searchTerm={searchTerm}
													timezone={timezone}
													onOpenContactDetails={onOpenContactDetails}
													onDismiss={dismissHandler}
													tabbableIndex={tabbableItem?.kind === "result-item" ? tabbableItem.index : 0}
												/>
											) :
											(
												<RecentSearchView
													frequentlyUsedAppsRef={frequentlyUsedAppsRef}
													nodes={nodes}
													onFocusInput={focusInputHandler}
													onSelectedNodeChanged={setSelectedNode}
													selectedNode={selectedNode}
													recentSearchQueries={actualRecentSearchQueries}
													recentSearchResults={recentlyOpenedItems}
													onDeleteSearchQuery={deleteSearchQueryHandler}
													onClearSearchQueries={clearSearchQueriesHandler}
													onRemoveRecentlyOpenedItem={removeRecentlyOpenedItem}
													onClearRecentlyOpenedItems={clearRecentlyOpenedItems}
													onSearch={searchHandler}
													onInvokeFrequentlyUsedTile={onInvokeFrequentlyUsedTile}
													onOpenContactDetails={onOpenContactDetails}
													onDismiss={dismissHandler}
													timezone={timezone}
													tabbableItem={tabbableItem}
													onTabbableItemChange={setTabbableItem}
													onFocusItemAtIndex={focusItemAtIndexHandler}
												/>
											)
									}
								</KeyBoardListNavigationContext.Provider>
								{ isBusy && <BusyIndicator /> }
							</DropOverContext.Provider>
							<div
								className={classNames(
									styles.resultsFooter,
									{[styles.resultsFooter_bordered]: shouldRenderBorder}
								)}
							>
								<Button
									kind="secondary"
									text={localize("_AdvancedSearch")}
									onClick={advancedSearchHandler}
									trackingId="advanced-search-button"
								/>
							</div>
						</div>
					)}
				</div>
			</div>
		);
	}
);
SearchAndResults.displayName = "SearchAndResults";

export type TabbableItem = {
	index: number;
	kind: "result-item" | "frequently-used-app";
};

export function getActualRecentSearchQueries(
	recentSearchQueries: readonly GlobalSearchTermModel[]
): readonly GlobalSearchTermModel[] {
	return sortBy(
		recentSearchQueries,
		x => x.lastUsed,
		SortDirection.Descending
	)
		.slice(0, 3);
}
